import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCheck, Download, Loader2, Save, Sparkles, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/activites-textes`;
const FILTRES = [
  ['propositions', 'Propositions à valider'], ['tous', 'À compléter'], ['definition', 'Sans définition'], ['exclusions', 'Sans exclusions'], ['complet', 'Complètes'],
];

const Editeur = ({ a, onFermer, onEnregistre }) => {
  const { getAuthHeader } = useAuth();
  const p = a.proposition;
  const [definition, setDefinition] = useState(p?.definition || a.definition || '');
  const [exclusions, setExclusions] = useState((p?.exclusions || a.exclusions || []).map((x) => `• ${x}`).join('\n'));
  const [busy, setBusy] = useState('');
  const [proposee, setProposee] = useState(!!p);

  const rejeter = async () => {
    setBusy('rej');
    try {
      await axios.delete(`${API}/serie/propositions/${p.id}`, { headers: getAuthHeader() });
      toast.success('Proposition rejetée');
      onEnregistre();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rejet impossible');
    } finally { setBusy(''); }
  };

  const proposer = async () => {
    setBusy('ia');
    try {
      const r = await axios.post(`${API}/${a.id}/proposition`, {}, { headers: getAuthHeader() });
      setDefinition(r.data.definition);
      setExclusions((r.data.exclusions || []).map((x) => `• ${x}`).join('\n'));
      setProposee(true);
      toast.success('Proposition de Klarys prête : relisez avant d’enregistrer');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Proposition impossible');
    } finally { setBusy(''); }
  };

  const enregistrer = async () => {
    setBusy('save');
    try {
      await axios.put(`${API}/${a.id}`, { definition, exclusions_texte: exclusions },
        { headers: getAuthHeader() });
      toast.success(`${a.code} enregistrée`);
      onEnregistre();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally { setBusy(''); }
  };

  return (
    <div className="space-y-3 rounded-xl border border-[#0a3d62]/20 bg-slate-50 p-4" data-testid={`activite-editeur-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold text-[#0a3d62]">{a.code} — {a.nom}</p>
        <button type="button" onClick={onFermer} className="text-slate-400 transition-colors hover:text-slate-700" data-testid="activite-editeur-fermer"><X className="h-4 w-4" /></button>
      </div>
      {proposee && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="activite-proposition-alerte">Texte proposé par Klarys : rien n’est enregistré tant que vous ne cliquez pas sur « Enregistrer ».</p>}
      <label className="block text-xs font-semibold text-slate-600">Définition
        <textarea value={definition} onChange={(e) => setDefinition(e.target.value)} rows={4} className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm font-normal" data-testid="activite-definition-input" />
      </label>
      <label className="block text-xs font-semibold text-slate-600">Exclusions (une par puce « • »)
        <textarea value={exclusions} onChange={(e) => setExclusions(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm font-normal" data-testid="activite-exclusions-input" />
      </label>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={proposer} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-full border border-[#DEA900] px-4 py-1.5 text-xs font-semibold text-[#8a6a00] transition-colors hover:bg-amber-50 disabled:opacity-50" data-testid="activite-proposer-btn">
          {busy === 'ia' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Proposition Klarys
        </button>
        <button type="button" onClick={enregistrer} disabled={!!busy || !definition.trim()} className="inline-flex items-center gap-1.5 rounded-full bg-[#0a3d62] px-4 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50" data-testid="activite-enregistrer-btn">
          {busy === 'save' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {p ? 'Valider et enregistrer' : 'Enregistrer'}
        </button>
        {p && (
          <button type="button" onClick={rejeter} disabled={!!busy} className="rounded-full px-4 py-1.5 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-50 disabled:opacity-50" data-testid="activite-rejeter-btn">
            Rejeter la proposition
          </button>
        )}
      </div>
    </div>
  );
};

const Ligne = ({ a, ouverte, onOuvrir, onFermer, onEnregistre }) => (
  <li className="py-2" data-testid={`activite-ligne-${a.id}`}>
    {ouverte ? <Editeur a={a} onFermer={onFermer} onEnregistre={onEnregistre} /> : (
      <button type="button" onClick={onOuvrir} className="flex w-full items-start justify-between gap-4 rounded-lg px-2 py-1 text-left transition-colors hover:bg-slate-50" data-testid={`activite-ouvrir-${a.id}`}>
        <span className="text-sm"><span className="font-mono text-xs text-slate-500">{a.code}</span> {a.nom}</span>
        <span className="flex shrink-0 gap-1 text-[10px] font-semibold">
          {a.proposition && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800" data-testid={`activite-badge-proposition-${a.id}`}>Proposition à valider</span>}
          <span className={`rounded px-1.5 py-0.5 ${a.definition ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{a.definition ? 'Définition ✓' : 'Sans définition'}</span>
          <span className={`rounded px-1.5 py-0.5 ${a.exclusions?.length ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{a.exclusions?.length ? 'Exclusions ✓' : 'Sans exclusions'}</span>
        </span>
      </button>
    )}
  </li>
);

const Serie = ({ categorie, libelle, nbPropositions, onFini }) => {
  const { getAuthHeader } = useAuth();
  const [st, setSt] = useState(null);
  const lire = useCallback(() => axios.get(`${API}/serie/statut`, { headers: getAuthHeader(), params: { categorie } })
    .then((r) => setSt(r.data)).catch(() => {}), [categorie, getAuthHeader]);
  useEffect(() => { lire(); }, [lire]);
  useEffect(() => {
    if (st?.statut !== 'en_cours') return undefined;
    const t = setInterval(lire, 4000);
    return () => clearInterval(t);
  }, [st?.statut, lire]);
  useEffect(() => { if (st?.statut === 'termine') onFini(); }, [st?.statut, st?.fin]); // eslint-disable-line react-hooks/exhaustive-deps

  const lancer = async () => {
    try {
      const r = await axios.post(`${API}/serie`, { categorie }, { headers: getAuthHeader() });
      setSt(r.data);
      toast.success(`Klarys prépare les textes « ${libelle} »`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Lancement impossible'); }
  };
  const [validant, setValidant] = useState(false);
  const validerTout = async () => {
    setValidant(true);
    try {
      const r = await axios.post(`${API}/serie/valider-tout`, { categorie }, { headers: getAuthHeader() });
      toast.success(`${r.data.validees} proposition(s) « ${libelle} » validée(s) et enregistrée(s)`);
      onFini();
    } catch (e) { toast.error(e.response?.data?.detail || 'Validation impossible'); } finally { setValidant(false); }
  };
  const enCours = st?.statut === 'en_cours';
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl bg-amber-50/60 px-4 py-3" data-testid="activites-serie">
      <button type="button" onClick={lancer} disabled={enCours} className="inline-flex items-center gap-1.5 rounded-full bg-[#DEA900] px-4 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50" data-testid="activites-serie-lancer">
        {enCours ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Préparer toute la famille « {libelle} »
      </button>
      {nbPropositions > 0 && (
        <button type="button" onClick={validerTout} disabled={validant || enCours} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 px-4 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50" data-testid="activites-valider-tout">
          {validant ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />} Tout valider ({nbPropositions})
        </button>
      )}
      {st?.statut && (
        <span className="text-xs text-slate-600" data-testid="activites-serie-statut">
          {enCours ? `En cours : ${st.faits + st.erreurs} / ${st.total || '…'}` : `Dernière préparation : ${st.faits} proposition(s)`}
          {st.erreurs ? ` · ${st.erreurs} échec(s)` : ''}
        </span>
      )}
    </div>
  );
};

/** Nomenclature : activités sans définition / exclusions, à compléter (avec propositions Klarys). */
export default function ActivitesACompleter() {
  const { getAuthHeader } = useAuth();
  const [f, setF] = useState({ manque: 'tous', categorie: '', q: '', page: 1 });
  const [data, setData] = useState(null);
  const [ouverte, setOuverte] = useState(null);

  const charger = useCallback(() => {
    axios.get(API, { headers: getAuthHeader(), params: f }).then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Chargement impossible'));
  }, [f, getAuthHeader]);
  useEffect(() => { charger(); }, [charger]);

  const [export_, setExport] = useState(false);
  const exporter = async () => {
    setExport(true);
    try {
      const r = await axios.get(`${API}/export`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = `Nomenclature_activites_${new Date().toISOString().slice(0, 10)}.xlsx`;
      lien.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export impossible'); } finally { setExport(false); }
  };
  const [import_, setImport] = useState(false);
  const importer = async (e) => {
    const fichier = e.target.files?.[0];
    e.target.value = '';
    if (!fichier) return;
    setImport(true);
    try {
      const fd = new FormData();
      fd.append('fichier', fichier);
      const r = await axios.post(`${API}/import`, fd, { headers: getAuthHeader() });
      const { modifiees, inchangees, introuvables } = r.data;
      toast.success(`Import terminé : ${modifiees} activité(s) mise(s) à jour, ${inchangees} inchangée(s)`
        + (introuvables.length ? ` · ${introuvables.length} ligne(s) non reconnue(s) : ${introuvables.slice(0, 5).join(', ')}` : ''),
      { duration: 10000 });
      charger();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import impossible'); } finally { setImport(false); }
  };
  const c = data?.compteurs || {};
  const pages = data ? Math.max(1, Math.ceil(data.total / data.par_page)) : 1;
  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="activites-a-completer">
      <div className="flex flex-wrap items-start gap-3">
        <div className="mr-auto">
        <h2 className="text-lg font-bold text-[#0a3d62]">Définitions et exclusions des activités</h2>
        <p className="text-sm text-slate-500" data-testid="activites-compteurs">{c.total ?? '…'} activités actives · {c.definition ?? '…'} sans définition · {c.exclusions ?? '…'} sans exclusions · {c.complet ?? '…'} complètes</p>
        </div>
        <button type="button" onClick={exporter} disabled={export_} className="inline-flex items-center gap-1.5 rounded-full border border-[#0a3d62] px-4 py-1.5 text-xs font-semibold text-[#0a3d62] transition-colors hover:bg-[#0a3d62] hover:text-white disabled:opacity-50" data-testid="activites-export-excel">
          {export_ ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Exporter en Excel
        </button>
        <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-[#0a3d62] px-4 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 ${import_ ? 'pointer-events-none opacity-50' : ''}`} data-testid="activites-import-excel">
          {import_ ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Importer le fichier relu
          <input type="file" accept=".xlsx" onChange={importer} className="hidden" data-testid="activites-import-input" />
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTRES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setF({ ...f, manque: k, page: 1 })} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${f.manque === k ? 'bg-[#0a3d62] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`activites-filtre-${k}`}>{l} ({c[k] ?? '…'})</button>
        ))}
        <select value={f.categorie} onChange={(e) => setF({ ...f, categorie: e.target.value, page: 1 })} className="rounded-lg border border-slate-200 px-2 py-1 text-xs" data-testid="activites-filtre-categorie">
          <option value="">Toutes les familles</option>
          {Object.entries(data?.categories || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value, page: 1 })} placeholder="Code ou libellé…" className="rounded-lg border border-slate-200 px-2 py-1 text-xs" data-testid="activites-recherche" />
      </div>
      {f.categorie && <Serie categorie={f.categorie} libelle={data?.categories?.[f.categorie] || f.categorie} nbPropositions={data?.propositions_famille || 0} onFini={charger} />}
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <ul className="divide-y divide-slate-100" data-testid="activites-liste">
          {data.items.map((a) => (
            <Ligne key={a.id} a={a} ouverte={ouverte === a.id} onOuvrir={() => setOuverte(a.id)} onFermer={() => setOuverte(null)}
              onEnregistre={() => { setOuverte(null); charger(); }} />
          ))}
          {!data.items.length && <li className="py-6 text-center text-sm text-slate-400">Aucune activité</li>}
        </ul>
      )}
      {pages > 1 && (
        <div className="flex items-center gap-2 text-xs" data-testid="activites-pagination">
          <button type="button" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} className="rounded border px-2 py-1 disabled:opacity-40" data-testid="activites-page-prec">Précédent</button>
          <span>Page {f.page} / {pages}</span>
          <button type="button" disabled={f.page >= pages} onClick={() => setF({ ...f, page: f.page + 1 })} className="rounded border px-2 py-1 disabled:opacity-40" data-testid="activites-page-suiv">Suivant</button>
        </div>
      )}
    </div>
  );
}
